/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { RootState } from 'store';

import { flightsExchangeClear, flightsExchangeOptions, flightsExchangeParameters } from 'action-creators/flights';
import { cancelPendingTravel } from 'actions/travelRequested';
import Card from 'components/Cards/CardTravel';
import LoaderCard from 'components/Cards/CardTravel/Loader';
import ConfirmationModal from 'components/ConfirmationModal';
import FlightsExchangeListContainer from 'containers/FlightsExchangeList';
import FlightsExchangeOptionContainer from 'containers/FlightsExchangeOption';
import FlightsExchangeSearchContainer from 'containers/FlightsExchangeSearch';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { FlightExchangeState } from 'models/flightExchange';
import { AirFareConditions } from 'models/flightValidation';
import { TravelRequested } from 'models/travelRequested';
import ResponseScreen, { BackgroundTypes, IconTypes } from 'screens/components/ResponseScreen';
import { TextModal } from 'screens/components/TravelDetails/components/Buttons/styles';
import TravelRequestedDetails from 'screens/containers/TravelRequestedDetails';
import { htmlT } from 'utils/generic';

import CardFlight from '../CardFlight';
import { ListContainer } from './styles';


interface Props {
  data: TravelRequested[];
  showExpirationTime?: boolean;
  showApprovedTag?: boolean;
  showRejectedTag?: boolean;
  type: 'travels' | 'requests';
  isLoading: boolean;
  hasFilterTags: boolean;
  fetchTravels?: () => void;
}

const ListTravels = ({
  data,
  showExpirationTime,
  showApprovedTag,
  showRejectedTag,
  type,
  isLoading,
  hasFilterTags,
  fetchTravels
}: Props) => {
  const [isShow, setIsShow] = useState(false);
  const [id, setId] = useState('');
  const [flight, setFlight] = useState<TravelRequested>();
  const { t } = useStoreTranslation();
  const [isShowConfirmCancel, setIsShowConfirmCancel] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isShowResultConfirmCancel, setIsShowResultConfirmCancel] = useState(false);
  const [resultConfirmCancelText, setResultConfirmCancelText] = useState('');
  const [resultConfirmCancelSubText, setResultConfirmCancelSubText] = useState(t('travelRequested.confirmCancel.success.subtext'));
  const userAccount = useSelector((state: RootState) => state.user?.accountResult);
  const [idForCancel, setIdForCancelTravelItineraryId] = useState('');
  const [showExchangeOption, setShowExchangeOption] = useState(false);
  const [showExchangeSearch, setShowExchangeSearch] = useState(false);
  const [showExchangeResult, setShowExchangeResult] = useState(false);
  const [changeDeparture, setChangeDeparture] = useState(false);
  const [changeArrival, setChangeArrival] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    history.listen((location) => {
      if (location.pathname === '/travelRequested') {
        dispatch(flightsExchangeClear());
        setShowExchangeOption(false);
        setShowExchangeSearch(false);
        setShowExchangeResult(false);
      }
      if (location.pathname === '/travelRequested/exchange-options') {
        setShowExchangeOption(true);
        setShowExchangeSearch(false);
        setShowExchangeResult(false);
      }
      if (location.pathname === '/travelRequested/exchange-search') {
        setShowExchangeOption(false);
        setShowExchangeSearch(true);
        setShowExchangeResult(false);
      }
      if (location.pathname === '/travelRequested/exchange-results') {
        setShowExchangeOption(false);
        setShowExchangeSearch(false);
        setShowExchangeResult(true);
      }
    });
  }, [dispatch, history]);

  const handleClick = (element: TravelRequested) => {
    setId(element.id);
    setIsShow(true);
    setFlight(element);
  };

  const handleOnCancel = (element: TravelRequested) => {
    setIdForCancelTravelItineraryId(element.id);
    setIsShowConfirmCancel(true);
  };

  const handleOnExchange = (element: TravelRequested, airFareConditions?: AirFareConditions[]) => {
    const flight: FlightExchangeState = {
      departureCode: element.departurePoint,
      arrivalCode: element.arrivalPoint,
      departureCity: element.flightDepartureCity,
      departureDate: new Date(element.date),
      arrivalCity: element.flightCity,
      arrivalDate: element.returnDate ? new Date(element.returnDate) : undefined,
      originalFlight: element,
      airFareConditions,
    }
    dispatch(flightsExchangeParameters(flight));
    history.push('/travelRequested/exchange-options');
  };

  const handleExchangeSubmit = () => {
    history.push('/travelRequested/exchange-results')
  }

  const handleCloseDetails = () => {
    setId('');
    setIsShow(false);
    setFlight(undefined);
  };

  const onCloseConfirmCancel = () => {
    setIdForCancelTravelItineraryId('');
    setIsShowConfirmCancel(false);
  };

  const onConfirmCancel = async () => {
    try {
      setIsCancelling(true);
      const {
        data: { errors, succeeded },
      } = await cancelPendingTravel(idForCancel, userAccount?.id ?? '', userAccount?.sessionToken ?? '');

      setIsCancelling(false);

      if (succeeded) {
        setIsShowConfirmCancel(false);

        setResultConfirmCancelText(t('travelRequested.confirmCancel.success.text'));
        setResultConfirmCancelSubText(t('travelRequested.confirmCancel.success.subtext'));
        setIsShowResultConfirmCancel(true);

      } else {
        if (errors?.indexOf('CannotCancel') !== -1) {
          setResultConfirmCancelText(t('travelRequested.confirmCancel.error.cannotCancel.text'));
          setResultConfirmCancelSubText(t('travelRequested.confirmCancel.error.cannotCancel.subtext'));
        } else {
          setResultConfirmCancelText(t('travelRequested.confirmCancel.error.genericText'));
          setResultConfirmCancelSubText(t('travelRequested.confirmCancel.error.genericSubText'));
        }

        setIsShowResultConfirmCancel(true);
      }

      setIsShowConfirmCancel(false);
      setIsShow(false);
      setIdForCancelTravelItineraryId('');

    } catch (error) {
      setResultConfirmCancelText(t('travelRequested.confirmCancel.error.genericText'));
      setResultConfirmCancelSubText(t('travelRequested.confirmCancel.error.genericSubText'));
      setIsShowResultConfirmCancel(true);
      setIsShowConfirmCancel(false);
    }
  };

  const onResultConfirmCancelClick = () => {
    onCloseConfirmCancel();
    setIsShowResultConfirmCancel(false);
    setIsShow(false);
    setIsCancelling(false);
    setIdForCancelTravelItineraryId('');

    history.push('/booking');
  }

  const handleExchangeContinue = (changeDeparture: boolean, changeArrival: boolean) => {
    dispatch(flightsExchangeOptions({ changeDeparture, changeArrival }));
    setChangeDeparture(changeDeparture);
    setChangeArrival(changeArrival);
    history.push('/travelRequested/exchange-search');
  }

  const handleGoBack = () => {
    history.goBack()
  };

  if (isLoading)
    return (
      <ListContainer>
        {Number(10)
          .toArray()
          .map((k) => (
            <LoaderCard key={k} />
          ))}
      </ListContainer>
    );

  return data?.length ? (
    <>
      <ListContainer data-testid="travel-list" className={hasFilterTags ? 'has-filters' : ''}>
        {data.map((element, index) => (
        <React.Fragment key={`${element.id}_${index}`}>
        {element.type === 'Flight' ? (
          <CardFlight
            showExpirationTime={showExpirationTime}
            showApprovedTag={showApprovedTag}
            showRejectedTag={showRejectedTag}
            travel={element}
            onClick={() => handleClick(element)}
            onCancel={() => handleOnCancel(element)}
            onChange={() => handleOnExchange(element)}
            testId={index === 0 ? 'travel-list-first-item' : undefined}
          />
        ) : (
          <Card
            showExpirationTime={showExpirationTime}
            showApprovedTag={showApprovedTag}
            showRejectedTag={showRejectedTag}
            travelRequested={element}
            onClick={() => handleClick(element)}
            onTravelCancel={() => handleOnCancel(element)}
            onChangeFlight={() => handleOnExchange(element)}
            testId={index === 0 ? 'travel-list-first-item' : undefined}
          />
        )}
        </React.Fragment>
        ))}
      </ListContainer>

      <TravelRequestedDetails
        id={id}
        show={isShow}
        onClose={handleCloseDetails}
        onChange={(airFareConditions?: AirFareConditions[]) => {
          if (flight) handleOnExchange(flight, airFareConditions)
        }}
        onCancel={() => {
          if (flight) handleOnCancel(flight)
        }}
      />

      <ConfirmationModal
        open={isShowConfirmCancel}
        title={t('travelRequested.confirmCancel.title')}
        onClose={onCloseConfirmCancel}
        onConfirm={onConfirmCancel}
        submitting={isCancelling}
        showCancelButton={true}
      >
        <TextModal>{htmlT(t, 'travelRequested.confirmCancel.text')}</TextModal>
      </ConfirmationModal>

      <ResponseScreen
        show={isShowResultConfirmCancel}
        text={resultConfirmCancelText}
        subText={resultConfirmCancelSubText}
        buttonText={t('travelRequested.confirmCancel.error.buttonText')}
        background={BackgroundTypes.Cloud}
        icon={IconTypes.Airplane}
        buttonOnClick={onResultConfirmCancelClick}
      />

      {showExchangeOption && (<FlightsExchangeOptionContainer show={showExchangeOption}
        onClose={handleGoBack} onContinue={handleExchangeContinue} />)}

      {showExchangeSearch && (<FlightsExchangeSearchContainer show={showExchangeSearch}
        onClose={() => history.push('/travelRequested')}
        onBack={handleGoBack}
        changeDeparture={changeDeparture}
        changeArrival={changeArrival}
        onSubmit={handleExchangeSubmit} />)}

      {showExchangeResult && (<FlightsExchangeListContainer show={showExchangeResult}
        onClose={() => history.push('/travelRequested')}
        onBack={handleGoBack}/>)}
    </>
  ) : null;
};

export default ListTravels;
